package dev.lingvo.site.pages.app.projects.slug.screenshots

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import dev.lingvo.shared.ApprovedRenderRef
import dev.lingvo.shared.CaptureDto
import dev.lingvo.shared.CoverageDto
import dev.lingvo.shared.DesignAssetDto
import dev.lingvo.shared.PixelSize
import dev.lingvo.shared.DeviceClass
import dev.lingvo.shared.RenderCheckReportDto
import dev.lingvo.shared.RenderDto
import dev.lingvo.shared.ScreenshotDesignDto
import dev.lingvo.shared.ScreenshotPlanDto
import dev.lingvo.shared.ShotPushJobDto
import dev.lingvo.shared.ShotPushPreflightDto
import dev.lingvo.shared.ShotPushRequest
import dev.lingvo.shared.ShotPushState
import dev.lingvo.shared.ShotSetApprovalDto
import dev.lingvo.shared.ShotSetApprovalRequest
import dev.lingvo.shared.StorePlatform
import dev.lingvo.shared.StoreShotsDto
import dev.lingvo.shared.screenshots.doc.DecodeResult
import dev.lingvo.shared.screenshots.doc.DesignCodec
import dev.lingvo.shared.screenshots.doc.ImageSource
import dev.lingvo.shared.screenshots.doc.ScreenshotDesign
import dev.lingvo.shared.screenshots.doc.StoreDeviceClassRule
import dev.lingvo.shared.screenshots.doc.TextContent
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.ApiException
import dev.lingvo.site.api.Async
import dev.lingvo.site.api.Session
import dev.lingvo.site.api.approveScreenshotSet
import dev.lingvo.site.api.cancelScreenshotPush
import dev.lingvo.site.api.checkScreenshotRenders
import dev.lingvo.site.api.currentScreenshotApproval
import dev.lingvo.site.api.loadReloadable
import dev.lingvo.site.api.pushScreenshotSet
import dev.lingvo.site.api.retryScreenshotPush
import dev.lingvo.site.api.revokeScreenshotApproval
import dev.lingvo.shared.ShotExportKind
import dev.lingvo.site.components.studio.ExportDownload
import dev.lingvo.site.api.screenshotCaptures
import dev.lingvo.site.api.screenshotCoverage
import dev.lingvo.site.api.screenshotDesign
import dev.lingvo.site.api.screenshotDesignAssets
import dev.lingvo.site.api.screenshotPlan
import dev.lingvo.site.api.screenshotPushJob
import dev.lingvo.site.api.screenshotPushJobs
import dev.lingvo.site.api.screenshotPushPreflight
import dev.lingvo.site.api.screenshotRenders
import dev.lingvo.site.api.screenshotStoreImages
import dev.lingvo.site.components.studio.fonts.FontDownloadProgress
import dev.lingvo.site.components.studio.fonts.FontLoadProgress
import dev.lingvo.site.components.studio.compose.CaptionCell
import dev.lingvo.site.components.studio.compose.ComposeBar
import dev.lingvo.site.components.studio.compose.ComposeCaption
import dev.lingvo.site.components.studio.compose.ComposeRunKey
import dev.lingvo.site.components.studio.compose.ComposeRunStore
import dev.lingvo.site.components.studio.compose.ComposeRunner
import dev.lingvo.site.components.studio.compose.EngineComposeRenderPort
import dev.lingvo.site.components.studio.compose.composeSha256
import dev.lingvo.site.components.studio.compose.fetchComposeImage
import dev.lingvo.site.components.studio.compose.planCompose
import dev.lingvo.site.components.studio.compose.screenshotComposeUploader
import dev.lingvo.site.ds.AsyncFailureState
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LSelect
import dev.lingvo.site.ds.LSelectOption
import dev.lingvo.site.ds.LoadingState
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.Space
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.nav.UrlState
import dev.lingvo.site.state.ProjectAuthority
import dev.lingvo.site.state.authoritySubject
import dev.lingvo.site.state.liveProjectRoute
import dev.lingvo.site.state.requestProjectResource
import kotlinx.browser.document
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.awaitCancellation
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.coroutines.yield
import org.w3c.dom.HTMLElement
import kotlinx.serialization.json.JsonObject
import org.jetbrains.compose.web.dom.Div

private data class ReviewData(
    val plan: ScreenshotPlanDto, val coverage: CoverageDto, val design: ScreenshotDesignDto?,
    val document: ScreenshotDesign?, val renders: List<RenderDto>, val checks: RenderCheckReportDto,
    val captures: List<CaptureDto>, val assets: List<DesignAssetDto>, val captions: Map<CaptionCell, ComposeCaption>,
    val approval: ShotSetApprovalDto?, val store: StoreShotsDto?, val job: ShotPushJobDto?, val partial: List<String>, val vaultUnconfigured: Boolean,
)

/** The page preserves the operator's job order and uses project claims for every privileged request. */
@Page("/app/projects/{slug}/screenshots/review")
@Layout(".components.layouts.AppLayout")
@Composable
fun ScreenshotReviewPage() {
    val context = rememberPageContext()
    val slug = context.route.params["slug"].orEmpty()
    val route = liveProjectRoute(slug)
    val subject = route?.authoritySubject(Session.organizations)
    val authority = ProjectAuthority.resolve(route?.project, Session.organizations)
    var params by remember(slug) { mutableStateOf(readScreenshotsAreaState(context)) }
    var reload by remember(subject, params.platform) { mutableStateOf(0) }
    val identity = Session.requestIdentity
    val key = ComposeRunKey(slug, params.platform, identity?.generation ?: -1)
    val store = remember(key) { ComposeRunStore.forArea(key) }
    DisposableEffect(key) { onDispose { ComposeRunStore.retireOutsideArea() } }
    var refreshedDone by remember(key) { mutableStateOf(0) }
    LaunchedEffect(store.progress.running, store.progress.done) {
        if (!store.progress.running && store.progress.done > refreshedDone) { refreshedDone = store.progress.done; reload++ }
    }
    val loaded = loadReloadable(subject to params.platform, reloadToken = reload) {
        val action = subject?.action ?: awaitCancellation()
        requestProjectResource(action, slug, Session.organizations) { loadReviewData(slug, params.platform) }
    }
    Column(Modifier.fillMaxWidth().gap(Space.XL).styleModifier { property("min-width", "0"); property("max-width", "100%") }) {
        PageHeader(t("app.projects.slug.screenshots.review.review_screenshots", "Review screenshots"))
        if (params.platform == StorePlatform.GOOGLE_PLAY) FeatureGraphicNotice()
        ScreenshotsAreaNav(slug, ScreenshotsTab.REVIEW, params, { params = it })
        ScreenshotsOfflineState()
        if (!authority.canWriteTranslations) ScreenshotsReadOnlyState()
        loaded.refreshError?.let { AsyncFailureState(it, subject = t("app.projects.slug.screenshots.review.the_screenshot_checks", "the screenshot checks"), onRetry = { reload++ }) }
        when (val state = loaded.state) {
            Async.Loading -> LoadingState(rows = 4)
            is Async.Failure -> AsyncFailureState(state, subject = t("app.projects.slug.screenshots.review.the_screenshot_plan", "the screenshot plan"), onRetry = { reload++ })
            is Async.Success -> ReviewContent(slug, params, state.value, store, authority.canWriteTranslations,
                authority.canEditStructure, reload, loaded.refreshing || loaded.refreshError != null, onParams = { params = it; UrlState.replace(it.toParams(ScreenshotsTab.REVIEW)) }, onReload = { reload++ })
        }
    }
}

/** Flex wrapping keeps the phone beside the summary on desktop and stacks it in DOM order on narrow screens. */
@Composable
fun ReviewLayout(compose: @Composable () -> Unit, simulator: @Composable () -> Unit, push: @Composable () -> Unit) {
    Div(Modifier.styleModifier { property("display", "grid"); property("gap", Space.XL.toString()); property("min-width", "0") }.toAttrs { attr("data-review-layout", "") }) {
        compose()
        Div(Modifier.styleModifier { property("display", "flex"); property("flex-wrap", "wrap"); property("gap", Space.XL.toString()); property("min-width", "0") }.toAttrs()) {
            Div(Modifier.styleModifier { property("flex", "1 1 390px"); property("max-width", "420px"); property("min-width", "0") }.toAttrs()) { simulator() }
            Div(Modifier.styleModifier { property("flex", "1 1 500px"); property("min-width", "0"); property("max-width", "100%") }.toAttrs()) { push() }
        }
    }
}

@Composable
private fun ReviewContent(
    slug: String, params: ScreenshotsAreaState, data: ReviewData, store: ComposeRunStore,
    canCompose: Boolean, canManage: Boolean, refreshEpoch: Int, validationPending: Boolean, onParams: (ScreenshotsAreaState) -> Unit, onReload: () -> Unit,
) {
    val scope = rememberCoroutineScope()
    val identity = Session.requestIdentity
    val actions = remember(slug, params.platform, identity) { ScreenshotsActionRunner(slug, { scope.launch { it() } }) }
    var busy by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var quota by remember { mutableStateOf<JsonObject?>(null) }
    var vaultOff by remember(data) { mutableStateOf(data.vaultUnconfigured) }
    var approval by remember(data.approval) { mutableStateOf(data.approval) }
    var job by remember(data.job) { mutableStateOf(data.job) }
    val rows = remember(data) { reviewRows(data.renders, data.checks, data.store) }
    var selected by remember(slug, params.platform) { mutableStateOf(defaultScreenshotPushSelection(rows)) }
    var knownSets by remember(slug, params.platform) { mutableStateOf(defaultScreenshotPushSelection(rows)) }
    LaunchedEffect(rows) {
        selected = refreshScreenshotSelection(rows, selected, knownSets)
        knownSets = defaultScreenshotPushSelection(rows)
    }
    val chosen = selected.intersect(defaultScreenshotPushSelection(rows))
    val requests = remember { ReviewRequestEpoch() }
    DisposableEffect(requests) { onDispose { requests.next() } }
    var preflight by remember(approval, chosen, refreshEpoch, data) { mutableStateOf<ShotPushPreflightDto?>(null) }
    fun fail(failure: Throwable) {
        error = failure.message
        if (failure is ApiException) {
            if (failure.code == "screenshot_quota_exceeded") quota = failure.details
            if (failure.code == "store_vault_unconfigured") vaultOff = true
        }
    }
    LaunchedEffect(approval, chosen, refreshEpoch, data, validationPending) {
        val request = requests.next()
        preflight = null
        val id = approval?.id ?: return@LaunchedEffect
        if (chosen.isEmpty() || !canManage || validationPending) return@LaunchedEffect
        actions.run(ScreenshotsPrivilege.PUSH, {}, {
            ApiClient.screenshotPushPreflight(slug, params.platform, id, chosen.map { it.locale }.distinct(), chosen.map { it.device }.distinct())
        }, { if (requests.isCurrent(request)) fail(it) }, { if (requests.isCurrent(request)) preflight = it })
    }
    LaunchedEffect(job?.id) {
        while (job?.state in setOf(ShotPushState.QUEUED, ShotPushState.RUNNING, ShotPushState.WAITING_FOR_VERSION)) {
            delay(3000)
            if (document.asDynamic().visibilityState != "visible") continue
            val id = job?.id ?: break
            try {
                val current = ApiClient.screenshotPushJob(slug, params.platform, id)
                if (Session.requestIdentity == identity && liveProjectRoute(slug) != null) job = current
            } catch (cancelled: CancellationException) { throw cancelled } catch (failure: Throwable) { fail(failure); break }
        }
    }
    val jobs = remember(data) { planCompose(data.plan, data.coverage, data.design?.version ?: 0, data.renders, data.captions,
        data.document, data.captures.associate { it.id to it.sha256 }) }
    val fontPort = remember(data) {
        data.document?.let { design -> EngineComposeRenderPort(design, { current, content, index ->
            val board = design.boards.first { it.id == current.boardId }
            val captionKey = when (content) {
                is TextContent.Key -> content.key
                is TextContent.SlotCaption -> board.pages.flatMap { it.slotIds }.getOrNull(index)
                is TextContent.Literal -> null
            }
            if (content is TextContent.Literal) content.text else captionKey?.let { data.captions[CaptionCell(it, current.locale)]?.text }
        }, { _, _, _ -> null }) }
    }
    var fontProgress by remember(data) { mutableStateOf<FontLoadProgress?>(null) }
    LaunchedEffect(fontPort, jobs) {
        try { fontPort?.ensureFonts(jobs) { fontProgress = it } } finally { fontProgress = null }
    }
    val locales = data.coverage.locales.map { it.locale }
    val locale = params.locale?.takeIf { it in locales } ?: locales.firstOrNull().orEmpty()
    val devices = data.plan.slots.flatMap { it.devices }.distinct()
    val device = params.device?.takeIf { it in devices } ?: devices.firstOrNull()
    val running = job?.state in setOf(ShotPushState.QUEUED, ShotPushState.RUNNING, ShotPushState.WAITING_FOR_VERSION)
    val usage = data.coverage.quota
    val storageFull = usage.projectUsedBytes >= usage.projectLimitBytes || usage.workspaceUsedBytes >= usage.workspaceLimitBytes
    UsageMeter(usage, slug, params)
    if (data.document == null) LAlert(t("app.projects.slug.screenshots.review.choose_and_save_a_design_in", "Choose and save a design in the studio before composing this set."), variant = LAlertVariant.INFO)
    data.partial.forEach { LAlert(it, variant = LAlertVariant.WARNING) }
    error?.let { LAlert(it, variant = LAlertVariant.DANGER) }
    quota?.let { ScreenshotsQuotaState(it) }
    if (vaultOff) ScreenshotsVaultOffState()
    if (running) ScreenshotsPushRunningState()
    if (data.store?.editable == false) ScreenshotsVersionLockedState(data.store.stateNote)
    LButton(t("app.projects.slug.screenshots.review.refresh_checks_and_approval", "Refresh checks and approval"), onReload, modifier = Modifier.attr("data-shot-refresh", ""), variant = LButtonVariant.SECONDARY, enabled = !busy)
    ExportDownload(slug, params.platform, ShotExportKind.RENDERS, enabled = data.renders.isNotEmpty())
    ReviewLayout(compose = {
        if (!store.progress.running) fontProgress?.let { FontDownloadProgress(it) }
        ComposeBar(store, jobs, data.checks, canCompose && !validationPending && !running && quota == null && data.document != null && !storageFull) {
            val design = data.document ?: return@ComposeBar
            val projectId = liveProjectRoute(slug)?.project?.id
            val orgId = liveProjectRoute(slug)?.project?.orgId
            val port = EngineComposeRenderPort(design, { current, content, index ->
                val board = design.boards.first { it.id == current.boardId }
                val key = when (content) { is TextContent.Key -> content.key; is TextContent.SlotCaption -> board.pages.flatMap { it.slotIds }.getOrNull(index); is TextContent.Literal -> null }
                if (content is TextContent.Literal) content.text else key?.let { data.captions[CaptionCell(it, current.locale)]?.text }
            }, { current, source, index ->
                val board = design.boards.first { it.id == current.boardId }
                suspend fun artwork(id: String): dynamic {
                    val asset = data.assets.first { it.id == id }
                    return fetchComposeImage(reviewBase(slug, params.platform) + "/assets/${encodeReview(id)}/image",
                        sourceSize = PixelSize(asset.width, asset.height), outputSize = PixelSize(current.width, current.height))
                }
                when (source) {
                    is ImageSource.Asset -> artwork(source.assetId)
                    ImageSource.BrandLogo -> design.brand.logoAssetId?.let { artwork(it) }
                    is ImageSource.Capture -> {
                        val slotKey = board.pages.flatMap { it.slotIds }.getOrNull(index)
                        val slot = data.plan.slots.firstOrNull { it.key == slotKey }
                        val language = if (slot?.localizedUi == false) data.coverage.locales.firstOrNull { it.isSource }?.locale else current.locale
                        data.captures.firstOrNull { it.slotId == slot?.id && it.deviceClass == current.device && it.locale == language && it.supersededAt == null }
                            ?.let { fetchComposeImage(reviewBase(slug, params.platform) + "/captures/${encodeReview(it.id)}/image",
                                sourceSize = PixelSize(it.width, it.height), outputSize = PixelSize(current.width, current.height)) }
                    }
                }
            })
            lateinit var runner: ComposeRunner
            runner = ComposeRunner(port, { current ->
                val captureId = requireNotNull(current.captureId)
                val capture = data.captures.first { it.id == captureId }
                fetchComposeImage(reviewBase(slug, params.platform) + "/captures/${encodeReview(captureId)}/image", runner::captureBlobCountChanged,
                    PixelSize(capture.width, capture.height), PixelSize(current.width, current.height))
            },
                screenshotComposeUploader(slug, params.platform, port::floorPx), {
                    Session.requestIdentity == identity && Session.isAuthenticated && (kotlinx.browser.window.location.pathname == "/app/projects/${encodeReview(slug)}/screenshots" || kotlinx.browser.window.location.pathname.startsWith("/app/projects/${encodeReview(slug)}/screenshots/")) && Session.organizations.any {
                        it.id == orgId && screenshotsPrivilegeAllowed(ScreenshotsPrivilege.COMPOSE, it.role, true)
                    } && projectId != null
                })
            store.start(runner, jobs)
        }
    }, simulator = {
        LSelect(device?.id.orEmpty(), devices.map { LSelectOption(it.id, screenshotDeviceLabel(it)) }, { value -> onParams(params.copy(device = DeviceClass.fromId(value))) },
            label = t("app.projects.slug.screenshots.review.preview_device", "Preview device"))
        StorePageSimulator(slug, params.platform, locale, locales, params.theme,
            data.renders.filter { it.locale == locale && it.deviceClass == device }.sortedBy { render -> data.plan.slots.indexOfFirst { it.id == render.slotId } },
            data.captions, liveProjectRoute(slug)?.project?.name.orEmpty(), { onParams(params.copy(platform = it, device = null)) },
            { onParams(params.copy(locale = it)) }, { onParams(params.copy(theme = it)) })
    }, push = {
        data.design?.version?.let { version ->
            if (device != null && locale.isNotBlank()) CanvasReviewPanel(slug, params.platform, version, data.plan.slots, device, locale,
                canComment = canCompose, canMark = canManage, refreshEpoch = refreshEpoch)
        }
        SetApprovalRevoke(approval, canManage, busy || validationPending, running, error) {
            val id = approval?.id ?: return@SetApprovalRevoke
            actions.run(ScreenshotsPrivilege.APPROVE, { busy = it }, {
                ApiClient.revokeScreenshotApproval(slug, params.platform, id)
            }, ::fail, {
                approval = null; preflight = null; onReload()
                scope.launch { yield(); (document.querySelector("[data-shot-refresh]") as? HTMLElement)?.focus() }
            })
        }
        PushReview(slug, params.platform, rows, selected, approval, if (validationPending) null else preflight, data.store, job, canManage, busy || validationPending,
            { selected = it }, {
                actions.run(ScreenshotsPrivilege.APPROVE, { busy = it }, {
                    ApiClient.approveScreenshotSet(slug, params.platform, ShotSetApprovalRequest(rows.filter { it.key in chosen }.flatMap { it.renders }.map { ApprovedRenderRef(it.id, it.sha256) }))
                }, ::fail, { approval = it })
            }, {
                val id = approval?.id ?: return@PushReview
                actions.run(ScreenshotsPrivilege.PUSH, { busy = it }, {
                    ApiClient.pushScreenshotSet(slug, params.platform, ShotPushRequest(id, chosen.map { it.locale }.distinct(), chosen.map { it.device }.distinct()))
                }, ::fail, { job = it })
            }, { job?.id?.let { id -> actions.run(ScreenshotsPrivilege.PUSH, { busy = it }, { ApiClient.cancelScreenshotPush(slug, params.platform, id) }, ::fail, { job = it }) } },
            { job?.id?.let { id -> actions.run(ScreenshotsPrivilege.PUSH, { busy = it }, { ApiClient.retryScreenshotPush(slug, params.platform, id) }, ::fail, { job = it }) } },
            renderChecks = reviewRenderChecks(slug, data.checks, chosen, data.plan.slots.associate { it.key to it.id }))
    })
    data.design?.version?.let { version ->
        ScreenshotReviewLinks(slug, params.platform, version, data.renders.filter { ReviewSetKey(it.locale, it.deviceClass) in chosen }, canManage)
    }
}

private suspend fun loadReviewData(slug: String, platform: StorePlatform): ReviewData {
    val partial = mutableListOf<String>()
    var vaultUnconfigured = false
    suspend fun <T> optional(block: suspend () -> T): T? = try { block() } catch (cancelled: CancellationException) { throw cancelled }
        catch (failure: ApiException) {
            if (failure.code == "store_vault_unconfigured") vaultUnconfigured = true
            else if (failure.status != 404) partial += failure.message.orEmpty()
            null
        }
    val plan = ApiClient.screenshotPlan(slug, platform)
    val coverage = requireNotNull(ApiClient.screenshotCoverage(slug, platform).first)
    val design = optional { ApiClient.screenshotDesign(slug, platform) }
    val decoded = design?.let { DesignCodec.decodeElement(it.document, StoreDeviceClassRule) }
    if (decoded is DecodeResult.Refused) partial += decoded.issues.joinToString(" ") { it.message }
    val captions = mutableMapOf<CaptionCell, ComposeCaption>()
    val file = ApiClient.listFiles(slug).firstOrNull { it.path == "store/${platform.id}/captions" }
    if (file != null) for (locale in coverage.locales) {
        var after: String? = null
        val seen = mutableSetOf<String>()
        do {
            val page = ApiClient.listKeys(slug, locale = locale.locale, fileId = file.id, after = after, limit = 200)
            page.items.forEach { row -> (if (locale.isSource) row.source else row.target)?.value?.let { text ->
                if (!row.key.isPlural) captions[CaptionCell(row.key.name, locale.locale)] = ComposeCaption(text, composeSha256(text.encodeToByteArray()))
            } }
            after = page.nextCursor?.takeIf { seen.add(it) }
        } while (after != null)
    }
    return ReviewData(plan, coverage, design, (decoded as? DecodeResult.Ok)?.design, ApiClient.screenshotRenders(slug, platform),
        ApiClient.checkScreenshotRenders(slug, platform), ApiClient.screenshotCaptures(slug, platform), ApiClient.screenshotDesignAssets(slug, platform), captions,
        optional { ApiClient.currentScreenshotApproval(slug, platform) }, optional { ApiClient.screenshotStoreImages(slug, platform) },
        optional { ApiClient.screenshotPushJobs(slug, platform) }?.firstOrNull(), partial, vaultUnconfigured)
}

private fun reviewBase(slug: String, platform: StorePlatform): String = "/projects/${encodeReview(slug)}/screenshots/${platform.id}"
private fun encodeReview(value: String): String = js("encodeURIComponent(value)") as String
